import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const LoginForm = () => {
  const navigate = useNavigate();
  const { signIn, authError, enableOfflineMode } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const result = await signIn(formData.email, formData.password);
      
      if (result.success) {
        navigate('/contract-upload');
      }
    } catch (error) {
      console.log('Login error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOfflineMode = () => {
    setIsSubmitting(true);
    
    try {
      enableOfflineMode();
      navigate('/contract-upload');
    } catch (error) {
      console.log('Offline mode error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-card/80 backdrop-blur-md border border-border/50 rounded-xl p-8 shadow-2xl">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
          Welcome Back
        </h1>
        <p className="text-muted-foreground font-body">
          Sign in to your ContractParser Pro account
        </p>
      </div>

      {/* Offline Mode Section */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-6 mb-6">
        <div className="text-center">
          <h3 className="text-lg font-heading font-semibold text-foreground mb-3">
            🚀 Test Contract Processing Now
          </h3>
          <p className="text-sm text-muted-foreground font-body mb-4">
            Skip database setup and test OpenAI contract processing immediately. Perfect for evaluating the core AI functionality without configuration.
          </p>
          <Button
            variant="default"
            size="sm"
            onClick={handleOfflineMode}
            disabled={isSubmitting}
            className="w-full"
          >
            {isSubmitting ? 'Starting Demo Mode...' : 'Start Contract Processing Demo'}
          </Button>
        </div>
      </div>

      {/* Demo Credentials */}
      <div className="bg-accent/5 border border-accent/20 rounded-lg p-4 mb-6">
        <h3 className="text-sm font-heading font-semibold text-foreground mb-2">
          Demo Credentials (Requires Database)
        </h3>
        <div className="space-y-2 text-xs text-muted-foreground font-body">
          <div>
            <span className="font-medium">Agent:</span> agent1@realestate.com / password123
          </div>
          <div>
            <span className="font-medium">Developer:</span> developer@contractparser.com / password123
          </div>
          <div>
            <span className="font-medium">Admin:</span> admin@contractparser.com / password123
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <Input
            label="Email Address"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            error={errors.email}
            placeholder="Enter your email"
            required
          />
        </div>

        <div>
          <Input
            label="Password"
            name="password"
            type="password"
            value={formData.password}
            onChange={handleInputChange}
            error={errors.password}
            placeholder="Enter your password"
            required
          />
        </div>

        {/* Error Display */}
        {authError && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
            <p className="text-destructive text-sm font-body">
              {authError}
            </p>
          </div>
        )}

        {/* Submit Button */}
        <Button
          type="submit"
          variant="outline"
          className="w-full"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Signing in...' : 'Sign In (Requires Database)'}
        </Button>

        {/* Forgot Password Link - Temporarily disabled */}
        {/*
        <div className="text-center">
          <Link
            to="/forgot-password"
            className="text-primary hover:text-primary/80 font-body text-sm font-medium transition-colors duration-200"
          >
            Forgot your password?
          </Link>
        </div>
        */}

        {/* Sign Up Link */}
        <div className="text-center">
          <p className="text-muted-foreground font-body text-sm">
            Don't have an account?{' '}
            <Link
              to="/signup"
              className="text-primary hover:text-primary/80 font-medium transition-colors duration-200"
            >
              Sign up
            </Link>
          </p>
        </div>
      </form>
    </div>
  );
};

export default LoginForm;